if (!customElements.get("madara-blog-section-anchor")) {
    customElements.define(
        "madara-blog-section-anchor",
        class MadaraBlogSectionAnchor extends HTMLElement {
            connectedCallback() {
                setTimeout(() => {
                    this.renderAnchors();
                    this.addStickyListener();
                }, 0);
            }

            disconnectedCallback() {
                if (this.scrollHandler) {
                    window.removeEventListener("scroll", this.scrollHandler);
                }

                if (this.sectionObserver) {
                    this.sectionObserver.disconnect();
                }
            }

            addStickyListener() {
                // Throttle function to limit scroll event frequency
                let ticking = false;

                const handleScroll = () => {
                    if (!ticking) {
                        requestAnimationFrame(() => {
                            // Get window scroll position
                            const windowScrollY =
                                window.pageYOffset || document.documentElement.scrollTop;

                            // Get article section offset position relative to document
                            const sectionAnchorRect = this.getBoundingClientRect();
                            const sectionAnchorOffsetTop =
                                sectionAnchorRect.top + windowScrollY;

                            // Calculate relative position (how far scrolled into the article)
                            const relativeScrollPosition =
                                windowScrollY - sectionAnchorOffsetTop;

                            if (relativeScrollPosition > -150) {
                                this.querySelector(".madara-blog-section-anchor").classList.add(
                                    "madara-blog-section-anchor--sticky"
                                );
                            } else {
                                this.querySelector(
                                    ".madara-blog-section-anchor"
                                ).classList.remove("madara-blog-section-anchor--sticky");
                            }

                            ticking = false;
                        });
                        ticking = true;
                    }
                };

                // Add scroll event listener
                window.addEventListener("scroll", handleScroll, { passive: true });

                // Store reference for cleanup
                this.scrollHandler = handleScroll;
            }

            renderAnchors() {
                const sectionList = document.querySelectorAll(".blog-section");
                const anchorMap = new Map(); // Map to store section-to-anchor relationships

                // Create anchor links for each section title
                sectionList.forEach((section, index) => {
                    const titleText = section
                        .querySelector(".blog-section__title")
                        ?.textContent?.trim();

                    // Create anchor element
                    if (titleText) {
                        const anchor = document.createElement("a");

                        anchor.href = `#${section.id}`;
                        anchor.textContent = titleText;
                        anchor.classList.add("madara-blog__section-anchor");

                        anchor.addEventListener("click", (e) => {
                            e.preventDefault();

                            const targetId = anchor.getAttribute("href").substring(1);
                            const targetElement = document.getElementById(targetId);

                            if (targetElement) {
                                const elementPosition =
                                    targetElement.getBoundingClientRect().top;
                                const offsetPosition =
                                    elementPosition + window.pageYOffset - 150;

                                window.scrollTo({
                                    top: offsetPosition,
                                    behavior: "smooth",
                                });
                            }
                        });

                        this.querySelector(".madara-blog-section-anchor").appendChild(
                            anchor
                        );

                        // Store the relationship between section and anchor
                        anchorMap.set(section, anchor);
                    }
                });

                // Set up Intersection Observer to track visible sections
                this.setupSectionObserver(sectionList, anchorMap);
            }

            setupSectionObserver(sections, anchorMap) {
                const options = {
                    root: null,
                    rootMargin: "-20% 0px -60% 0px", // Trigger when section is 20% from top and 60% from bottom
                    threshold: 0.05, // Trigger when 5% of section is visible
                };

                const observer = new IntersectionObserver((entries) => {
                    entries.forEach((entry) => {
                        const anchor = anchorMap.get(entry.target);
                        if (anchor) {
                            if (entry.isIntersecting) {
                                // Remove active class from all anchors
                                anchorMap.forEach((anchorElement) => {
                                    anchorElement.classList.remove(
                                        "madara-blog__section-anchor--active"
                                    );
                                });

                                // Add active class to current anchor
                                anchor.classList.add("madara-blog__section-anchor--active");

                                // Broadcast active section change so other components
                                // (e.g. the mobile sticky selector) can stay in sync.
                                document.dispatchEvent(
                                    new CustomEvent("madara-blog:active-section-change", {
                                        detail: {
                                            sectionId: entry.target.id,
                                            title: anchor.textContent,
                                        },
                                    })
                                );
                            }
                        }
                    });
                }, options);

                // Start observing all sections
                sections.forEach((section) => {
                    observer.observe(section);
                });

                // Store observer reference for cleanup
                this.sectionObserver = observer;
            }
        }
    );
}

if (!customElements.get("madara-blog-section-anchor-sticky-selector")) {
    customElements.define(
        "madara-blog-section-anchor-sticky-selector",
        class MadaraBlogSectionAnchorStickySelector extends HTMLElement {
            connectedCallback() {
                this.toggleButton = this.querySelector(
                    ".madara-blog-section-anchor-sticky-selector__toggle"
                );
                this.labelElement = this.querySelector(
                    ".madara-blog-section-anchor-sticky-selector__label"
                );
                this.listElement = this.querySelector(
                    ".madara-blog-section-anchor-sticky-selector__list"
                );
                this.defaultLabel = this.labelElement
                    ? this.labelElement.textContent.trim()
                    : "";
                this.optionMap = new Map(); // sectionId -> option button

                setTimeout(() => {
                    this.renderOptions();
                    this.bindEvents();
                }, 0);
            }

            disconnectedCallback() {
                if (this.toggleButton && this.onToggleClick) {
                    this.toggleButton.removeEventListener("click", this.onToggleClick);
                }
                if (this.onDocumentClick) {
                    document.removeEventListener("click", this.onDocumentClick);
                }
                if (this.onWindowScroll) {
                    window.removeEventListener("scroll", this.onWindowScroll);
                }
                this.releaseActiveLock();
                if (this.onActiveSectionChange) {
                    document.removeEventListener(
                        "madara-blog:active-section-change",
                        this.onActiveSectionChange
                    );
                }
            }

            renderOptions() {
                if (!this.listElement) return;

                const sectionList = document.querySelectorAll(".blog-section");

                sectionList.forEach((section) => {
                    const titleText = section
                        .querySelector(".blog-section__title")
                        ?.textContent?.trim();

                    if (!titleText || !section.id) return;

                    const li = document.createElement("li");
                    const option = document.createElement("button");
                    option.type = "button";
                    option.classList.add(
                        "madara-blog-section-anchor-sticky-selector__option"
                    );
                    option.textContent = titleText;
                    option.dataset.sectionId = section.id;
                    option.setAttribute("role", "option");

                    option.addEventListener("click", () => {
                        this.selectSection(section.id, titleText);
                        this.close();
                    });

                    li.appendChild(option);
                    this.listElement.appendChild(li);
                    this.optionMap.set(section.id, option);
                });
            }

            bindEvents() {
                if (!this.toggleButton) return;

                this.onToggleClick = (e) => {
                    e.stopPropagation();
                    this.isOpen() ? this.close() : this.open();
                };
                this.toggleButton.addEventListener("click", this.onToggleClick);

                this.onDocumentClick = (e) => {
                    if (this.isOpen() && !this.contains(e.target)) {
                        this.close();
                    }
                };
                document.addEventListener("click", this.onDocumentClick);

                // Close the dropdown when the user scrolls the page while it's open.
                // We capture the scroll position at open-time and only close once the
                // user has scrolled past a small threshold, so that the act of opening
                // (which can cause sub-pixel layout shifts) doesn't immediately close it.
                this.onWindowScroll = () => {
                    if (!this.isOpen()) return;

                    const currentScrollY =
                        window.pageYOffset || document.documentElement.scrollTop;
                    const delta = Math.abs(currentScrollY - (this.scrollYAtOpen || 0));

                    if (delta > 5) {
                        this.close();
                    }
                };
                window.addEventListener("scroll", this.onWindowScroll, {
                    passive: true,
                });

                this.onActiveSectionChange = (e) => {
                    // While the user has just clicked an option and the page is
                    // smooth-scrolling to that section, ignore intersection-driven
                    // active section changes so the chosen option stays active.
                    if (this.lockActiveOption) return;

                    const { sectionId, title } = e.detail || {};
                    if (sectionId) {
                        this.setActiveOption(sectionId, title);
                    }
                };
                document.addEventListener(
                    "madara-blog:active-section-change",
                    this.onActiveSectionChange
                );
            }

            selectSection(sectionId, title) {
                const targetElement = document.getElementById(sectionId);
                if (!targetElement) return;

                const elementPosition = targetElement.getBoundingClientRect().top;
                // Account for the sticky selector height so the section title is not
                // hidden underneath it after scrolling.
                const stickyOffset = this.offsetHeight ? this.offsetHeight + 20 : 80;
                const headerHeight = 60;
                const offsetPosition =
                    elementPosition + window.pageYOffset - headerHeight - stickyOffset;

                // Lock the active option to the user's choice while the
                // programmatic smooth scroll is running. The lock is released:
                //   - as soon as the user manually interacts (wheel, touch, key)
                //   - or when the programmatic scroll reaches its target
                //   - or after a 1s safety timeout
                this.releaseActiveLock();
                this.lockActiveOption = true;

                const releaseLock = () => this.releaseActiveLock();

                this.unlockUserScrollHandlers = ["wheel", "touchmove", "keydown"];
                this.unlockUserScrollHandlers.forEach((evt) => {
                    window.addEventListener(evt, releaseLock, {
                        passive: true,
                        once: true,
                    });
                });
                this.unlockUserScrollListener = releaseLock;

                this.unlockScrollHandler = () => {
                    if (this.unlockTimeout) clearTimeout(this.unlockTimeout);
                    this.unlockTimeout = setTimeout(() => {
                        const currentY =
                            window.pageYOffset || document.documentElement.scrollTop;
                        if (Math.abs(currentY - offsetPosition) < 2) {
                            this.releaseActiveLock();
                        }
                    }, 100);
                };
                window.addEventListener("scroll", this.unlockScrollHandler, {
                    passive: true,
                });

                // Safety net in case no scroll happens (e.g. target already in view).
                this.unlockTimeout = setTimeout(() => {
                    this.releaseActiveLock();
                }, 1000);

                window.scrollTo({
                    top: offsetPosition,
                    behavior: "smooth",
                });

                this.setActiveOption(sectionId, title);
            }

            releaseActiveLock() {
                this.lockActiveOption = false;
                if (this.unlockTimeout) {
                    clearTimeout(this.unlockTimeout);
                    this.unlockTimeout = null;
                }
                if (this.unlockScrollHandler) {
                    window.removeEventListener(
                        "scroll",
                        this.unlockScrollHandler
                    );
                    this.unlockScrollHandler = null;
                }
                if (this.unlockUserScrollHandlers && this.unlockUserScrollListener) {
                    this.unlockUserScrollHandlers.forEach((evt) => {
                        window.removeEventListener(
                            evt,
                            this.unlockUserScrollListener
                        );
                    });
                    this.unlockUserScrollHandlers = null;
                    this.unlockUserScrollListener = null;
                }
            }

            setActiveOption(sectionId, title) {
                this.optionMap.forEach((option) => {
                    option.classList.remove(
                        "madara-blog-section-anchor-sticky-selector__option--active"
                    );
                });

                const activeOption = this.optionMap.get(sectionId);
                if (activeOption) {
                    activeOption.classList.add(
                        "madara-blog-section-anchor-sticky-selector__option--active"
                    );
                }

                if (this.labelElement) {
                    const labelText =
                        title ||
                        (activeOption ? activeOption.textContent : this.defaultLabel);
                    this.labelElement.textContent = labelText;
                }
            }

            isOpen() {
                return this.hasAttribute("open");
            }

            open() {
                this.setAttribute("open", "");
                if (this.listElement) this.listElement.hidden = false;
                if (this.toggleButton) {
                    this.toggleButton.setAttribute("aria-expanded", "true");
                }
                this.scrollYAtOpen = window.pageYOffset || document.documentElement.scrollTop;
            }

            close() {
                this.removeAttribute("open");
                if (this.listElement) this.listElement.hidden = true;
                if (this.toggleButton) {
                    this.toggleButton.setAttribute("aria-expanded", "false");
                }
            }
        }
    );
}
